import React, { useState, useEffect, useMemo } from 'react';
import Header from '../Components/Header';
import Footer from '../Components/Footer';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import './HomePage.css';
import './LiveAuctions.css';

const formatMileage = (miles) => {
  if (!miles) return '0';
  const numericValue = miles.toString().replace(/[^0-9]/g, '');
  return numericValue.replace(/\B(?=(?:\d{3})+(?!\d))/g, ',');
};

const LiveAuctions = () => {
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchLive = async () => {
      try {
        const auctionsRef = collection(db, 'auctions');
        const q = query(auctionsRef, where('finalBid', '==', null));
        const snapshot = await getDocs(q);
        const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        data.sort((a, b) => {
          const at = a.auctionDate?.seconds ?? 0;
          const bt = b.auctionDate?.seconds ?? 0;
          return bt - at;
        });
        setAuctions(data);
      } catch (err) {
        console.error('Error fetching live auctions:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchLive();
  }, []);

  const filteredAuctions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return auctions;
    return auctions.filter((a) => {
      const haystack = [
        a.title,
        a.vin,
        a.exteriorColor,
        a.interiorColor,
        a.auctionHouse,
        a.year,
        a.modelId,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [auctions, searchTerm]);

  return (
    <div className="betasml-homepage">
      <Header
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        showSearch={true}
      />
      <main className="live-auctions-main">
        <section className="live-auctions-section">
          <div className="live-auctions-header">
            <h1 className="live-auctions-title">Live Auctions</h1>
            <p className="live-auctions-subtitle">
              {loading
                ? 'Loading live listings…'
                : `${filteredAuctions.length} active listing${
                    filteredAuctions.length === 1 ? '' : 's'
                  }`}
            </p>
          </div>

          <div className="model-grid live-auctions-grid">
            {!loading && filteredAuctions.length > 0 ? (
              filteredAuctions.map((auction) => {
                const Wrapper = auction.auctionLink ? 'a' : 'div';
                const title =
                  auction.title ||
                  [auction.year, auction.modelId].filter(Boolean).join(' ') ||
                  'Live auction';
                return (
                  <Wrapper
                    key={auction.id}
                    {...(auction.auctionLink
                      ? {
                          href: auction.auctionLink,
                          target: '_blank',
                          rel: 'noopener noreferrer',
                        }
                      : {})}
                    className="model-tile live-auction-glow"
                    style={{ cursor: auction.auctionLink ? 'pointer' : 'default' }}
                  >
                    <div className="model-image-container">
                      <img
                        src={auction.imageLink}
                        alt={title}
                        className="model-image"
                      />
                    </div>
                    <div className="model-info">
                      <h3>{title}</h3>
                      <p>
                        {auction.miles ? `${formatMileage(auction.miles)} miles` : ''}
                        {auction.miles && auction.auctionHouse ? ' • ' : ''}
                        {auction.auctionHouse || ''}
                      </p>
                    </div>
                  </Wrapper>
                );
              })
            ) : (
              <p
                style={{
                  color: '#666',
                  fontSize: '16px',
                  gridColumn: '1 / -1',
                  textAlign: 'center',
                  padding: '40px',
                }}
              >
                {loading ? '' : 'No live auctions at this time'}
              </p>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default LiveAuctions;
